import React from 'react';
import {
  Trophy,
  CalendarClock,
} from 'lucide-react-native';
import {
  Box,
  HStack,
  Text,
  Icon,
  Pressable,
} from '@gluestack-ui/themed';
import type { Task } from '@/common/api/models/task';
import TaskItemOptions from './TaskItemOptions';

const TaskGradingRelevantBadge = () => (
  <Box
    ml="$1.5"
    px="$1"
    py="$0.5"
    borderRadius="$xs"
    bg="$bgBrandSubtle"
  >
    <Icon as={Trophy} size="2xs" color="$brandStrong" />
  </Box>
);

const EventTaskBadge = () => (
  <Box
    ml="$1.5"
    px="$1"
    py="$0.5"
    borderRadius="$xs"
    bg="$statusWarningSubtle"
  >
    <Icon as={CalendarClock} size="2xs" color="$statusWarning" />
  </Box>
);

interface TaskItemProps {
  task: Task;
  isSelected: boolean;
  isFirstOfLevel: boolean;
  isLastOfLevel: boolean;
  readonly: boolean;
  onSelect: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  testID?: string;
}

/**
 * Task item - indented under level, tight vertical rhythm
 */
const TaskItem: React.FC<TaskItemProps> = React.memo(({
  task,
  isSelected,
  isFirstOfLevel,
  isLastOfLevel,
  readonly,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDelete,
  testID,
}) => (
  <Box
    flexDirection="row"
    alignItems="center"
    justifyContent="space-between"
    ml="$4"
    mr="$1.5"
    pl={isSelected ? 9 : 12}
    borderLeftWidth={isSelected ? 3 : 0}
    borderLeftColor={isSelected ? '$brandBase' : 'transparent'}
    bg="transparent"
  >
    <Pressable
      onPress={onSelect}
      flex={1}
      py="$2"
      testID={testID || `task-item-${task.id}`}
    >
      <HStack alignItems="center" flex={1}>
        <Text
          numberOfLines={1}
          size="sm"
          fontWeight={isSelected ? 'semibold' : 'normal'}
          color={isSelected ? '$textHeading' : '$textBody'}
          fontStyle={!task.title ? 'italic' : 'normal'}
          flex={1}
        >
          {task.title || '<Untitled Task>'}
        </Text>
        {task.evaluation?.gradingRelevant && <TaskGradingRelevantBadge />}
        {task.type === 'event' && <EventTaskBadge />}
      </HStack>
    </Pressable>
    <Box w={28} alignItems="center">
      {isSelected && (
        <TaskItemOptions
          readonly={readonly}
          isFirstOfLevel={isFirstOfLevel}
          isLastOfLevel={isLastOfLevel}
          operations={{ onMoveUp, onMoveDown, onDelete }}
        />
      )}
    </Box>
  </Box>
));

export default TaskItem;
